/**
 * Client build / version stamp (E1.6.S1 — implements the `docs/ops/environments.md`
 * "Version stamping" spec). The build sha is a **client-build-local** constant:
 * `frontend/vite.config.ts` resolves it at build time (env override →
 * `RENDER_GIT_COMMIT` → `git rev-parse HEAD` → `'unstamped'`) and injects it via
 * Vite `define` as the global `__GC_BUILD_SHA__`. Nothing is sent on the wire —
 * this is presentation only (AD-124/125 hold; NOT in `net/platform.ts`).
 *
 * Everything here is pure and framework-free so BOTH `vite.config.ts` (Node, at
 * build time) and the AD-120 test harness (`node --test`) can import it:
 *   - `resolveBuildSha()` — the build-time resolution ORDER (called by vite.config.ts).
 *   - `buildSha()` / display helpers — read the injected constant in the browser,
 *     falling back to `'unstamped'` when the `define` is absent (tsc / node --test).
 */

// Injected by Vite `define` at build time (see vite.config.ts). Absent under
// `tsc` / `node --test` — the `typeof` guard in `buildSha()` yields the fallback then.
declare const __GC_BUILD_SHA__: string | undefined;

/** The literal stamp used whenever no real sha is available (never build-fatal). */
export const UNSTAMPED = 'unstamped';

/** Build-time resolution inputs, each already read from its source by the caller. */
export interface BuildShaSources {
  /** Explicit override — `GC_BUILD_SHA`. Wins over everything. */
  override?: string | null;
  /** Render's deploy commit — `RENDER_GIT_COMMIT` (present on Render builds). */
  renderCommit?: string | null;
  /** `git rev-parse HEAD`, or `null` when git / `.git` is unavailable (archive copy). */
  gitSha?: string | null;
}

/**
 * Build-time resolution order: the first non-empty of `override` → `renderCommit`
 * → `gitSha`, trimmed; else `'unstamped'`. Pure and total — never throws, so a
 * `.git`-less archive build (`gitSha` null) still yields a valid stamp and the
 * build never fails for want of a sha.
 */
export function resolveBuildSha(sources: BuildShaSources): string {
  for (const candidate of [sources.override, sources.renderCommit, sources.gitSha]) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed !== '') return trimmed;
    }
  }
  return UNSTAMPED;
}

/**
 * The build-injected stamp, read in the client. Returns `'unstamped'` when the
 * `define` was not applied (e.g. under the test harness). `typeof` on an
 * undeclared identifier is the one safe probe — it never throws a ReferenceError.
 */
export function buildSha(): string {
  if (typeof __GC_BUILD_SHA__ === 'string') {
    const value = __GC_BUILD_SHA__.trim();
    if (value !== '') return value;
  }
  return UNSTAMPED;
}

/** Short display form (7 chars) for a real sha; `'unstamped'` passes through unchanged. */
export function shortSha(sha: string = buildSha()): string {
  return sha === UNSTAMPED ? sha : sha.slice(0, 7);
}

/**
 * Whether the server answers from a different build than this client's own stamp
 * (q-live-sword-grip-still-open): the deploy stamps both with the same sha (`/healthz` `sha`
 * and `__GC_BUILD_SHA__`), compared over their common prefix (12 characters on the box; at least
 * 7). `false` whenever either side cannot say: an unstamped client, or a missing, `unknown` or
 * `unstamped` served value. Unknown is never treated as stale, so it can never cause a reload.
 */
export function isStaleBuild(
  served: string | null | undefined,
  client: string = buildSha(),
): boolean {
  const a = (served ?? '').trim().toLowerCase();
  const b = client.trim().toLowerCase();
  const unknown = (s: string): boolean => s === '' || s === UNSTAMPED || s === 'unknown';
  if (unknown(a) || unknown(b)) return false;
  const n = Math.min(a.length, b.length);
  if (n < 7) return false;
  return a.slice(0, n) !== b.slice(0, n);
}

/** The one-line boot banner text (build sha + mode) for the `console.info` line. */
export function buildBannerLine(mode = 'unknown'): string {
  return `The Great Cloud client — build ${shortSha()} · ${mode}`;
}

/**
 * Emit the boot banner once (the E1.6 `console.info` boot line). The logger is
 * injectable for testing; it defaults to `console.info`.
 */
export function logBuildBanner(
  mode = 'unknown',
  log: (message: string) => void = (message) => {
    console.info(message);
  },
): void {
  log(buildBannerLine(mode));
}
